import type { CriterionScoring, CriterionScoringDraft } from '../schemas/criterion.js';
import { CRITERION_SCORING_BANDS_MIN, CRITERION_SCORING_TOP } from '../schemas/criterion.js';

/**
 * The scale of a criterion, as arithmetic (refonte modulaire §6, grill of
 * 2026-09-16).
 *
 * Two things live here and nowhere else.
 *
 * ## The lower bound is DERIVED, so it is derived in one place
 *
 * « La borne basse n'est pas saisie : c'est la borne haute de la tranche
 * précédente, 0 pour la première » (§6). The editor paints it in grey, the
 * scribe's prompt writes it in a line, the fiche of an objective reads the one
 * of the plancher. Three readers, one rule - and a second implementation would
 * be the place where a scale means one thing on the screen and another in the
 * prompt.
 *
 * ## The editor greys, the server refuses, and they judge the same thing
 *
 * `scoringProblems` is what the editor closes its button on and underlines a
 * band with; `criterionScoringSchema` is what the server answers 400 to. They
 * are two implementations of one rule on purpose - a Zod refinement cannot name
 * the band to a `role="alert"`, and a pure walk cannot be a trust boundary -
 * and `tests/criterion.test.ts` holds them together kind by kind. The failure
 * this guards against is precise: a draft the editor accepts and the server
 * refuses is a save that fails with nothing on the screen to repair.
 */

/** What is wrong with a scale, in the vocabulary the editor underlines with. */
export type CriterionScoringProblem =
  /** Fewer bands than `CRITERION_SCORING_BANDS_MIN` - a scale needs two. */
  | { readonly kind: 'too-few-bands' }
  /** The plancher names a band the list does not have. */
  | { readonly kind: 'plancher-out-of-range' }
  /** A passable band marked at or above the plancher (issue #110). */
  | { readonly kind: 'passable-not-below-plancher' }
  /** A band whose upper bound has not been typed yet. */
  | { readonly kind: 'missing-upper'; readonly band: number }
  /** A band whose upper bound does not clear the previous one. */
  | { readonly kind: 'non-increasing-upper'; readonly band: number }
  /** The last band ends somewhere other than 100. */
  | { readonly kind: 'last-not-100'; readonly band: number }
  /** A band with no description - §6 makes it required. */
  | { readonly kind: 'empty-text'; readonly band: number };

/** The problems that name a band - the ones a row is underlined and captioned with. */
export type CriterionBandProblem = Extract<CriterionScoringProblem, { band: number }>;

/** The problems of the scale as a WHOLE - captioned under the section, not under a row. */
export type CriterionScaleProblem = Exclude<CriterionScoringProblem, { band: number }>;

/** The bands a scale is made of, in the two shapes this module is handed. */
type Band = { readonly upper: number | null };

/**
 * The lower bound of each band, in order: 0 for the first, the previous band's
 * upper for every other.
 *
 * `null` where it cannot be known - a previous band whose upper has not been
 * typed - which is exactly the hatched zone of the editor's preview: « la
 * tranche 3 est sans fin, la tranche 4 sans début ».
 */
export function bandLowerBounds(bands: readonly { readonly upper: number }[]): number[];
export function bandLowerBounds(bands: readonly Band[]): (number | null)[];
export function bandLowerBounds(bands: readonly Band[]): (number | null)[] {
  return bands.map((_band, index) => (index === 0 ? 0 : (bands[index - 1]?.upper ?? null)));
}

/**
 * The score from which a scored criterion is filled, with no threshold posed by
 * an objective (§6, §7.2): the LOWER bound of the plancher band.
 *
 * The lower and not the upper, and the difference is the whole meaning of the
 * word: a band runs from its lower bound included to its upper excluded, so
 * « rempli à partir de la tranche 50-75 » is filled at 50.
 */
export function plancherBound(scoring: CriterionScoring): number {
  return bandLowerBounds(scoring.bands)[scoring.plancher] ?? 0;
}

/**
 * The score from which an answer is « passable » (§6, issue #110): the LOWER
 * bound of the passable band, for the reason `plancherBound` gives - or null
 * when the scale marks none, which is a scale read in two tones as before.
 */
export function passableBound(scoring: CriterionScoring): number | null {
  if (scoring.passable === null) return null;
  return bandLowerBounds(scoring.bands)[scoring.passable] ?? 0;
}

/**
 * Everything wrong with a scale being typed, band by band.
 *
 * The order is the order of reading - the bands top to bottom, then what is
 * true of the whole - because the editor shows the first problem of a band
 * under its row, and a list that named the plancher before the band above it
 * would send the eye backwards.
 *
 * An empty list means this draft WOULD parse as a version's content, which is
 * what arms « Enregistrer une nouvelle version ».
 */
export function scoringProblems(scoring: CriterionScoringDraft): CriterionScoringProblem[] {
  const problems: CriterionScoringProblem[] = [];
  const lowers = bandLowerBounds(scoring.bands);

  scoring.bands.forEach((band, index) => {
    if (band.upper === null) {
      problems.push({ kind: 'missing-upper', band: index });
    } else {
      /*
        A band whose PREVIOUS upper is missing is not itself out of order: the
        hole is the neighbour's, already reported, and saying it twice would
        underline two rows for one defect. Hence the `null` guard rather than a
        `?? 0`, which would have compared against the floor of the scale and
        called a perfectly ordered band non-increasing.
      */
      const lower = lowers[index];
      if (lower !== null && lower !== undefined && band.upper <= lower) {
        problems.push({ kind: 'non-increasing-upper', band: index });
      }
      if (index === scoring.bands.length - 1 && band.upper !== CRITERION_SCORING_TOP) {
        problems.push({ kind: 'last-not-100', band: index });
      }
    }
    /*
      TRIMMED before it is judged, and it matters: the editor holds what was
      typed, the schema trims before requiring a description, so a band holding
      three spaces would otherwise pass here and be refused there - the exact
      disagreement this module exists to make impossible.
    */
    if (band.text.trim() === '') problems.push({ kind: 'empty-text', band: index });
  });

  if (scoring.bands.length < CRITERION_SCORING_BANDS_MIN) problems.push({ kind: 'too-few-bands' });
  if (scoring.plancher >= scoring.bands.length) problems.push({ kind: 'plancher-out-of-range' });
  if (scoring.passable !== null && scoring.passable >= scoring.plancher) {
    problems.push({ kind: 'passable-not-below-plancher' });
  }

  return problems;
}

/**
 * The first problem that names a band, or null - what a row is underlined and
 * captioned with.
 *
 * The FIRST and not all of them: a band with no upper bound and no description
 * is one band to repair, and two captions under one row would read as two
 * defects.
 */
export function bandProblem(
  problems: readonly CriterionScoringProblem[],
  band: number,
): CriterionBandProblem | null {
  return (
    problems.find(
      (problem): problem is CriterionBandProblem => 'band' in problem && problem.band === band,
    ) ?? null
  );
}

/**
 * The first problem that names NO band, or null - what the section is
 * captioned with, under its rows.
 *
 * None of the three is reachable from the editor, which floors the list at two
 * bands, only ever sets the plancher from a radio, and only offers a passable
 * band below the plancher (moving the plancher under it clears it). They ARE
 * reachable from a
 * draft written elsewhere - the MCP assistant of §20 posts contents this screen
 * then opens - and a greyed button with no sentence anywhere is exactly the
 * « save that fails with nothing on the screen to repair » this module exists
 * to prevent.
 */
export function scaleProblem(
  problems: readonly CriterionScoringProblem[],
): CriterionScaleProblem | null {
  return problems.find((problem): problem is CriterionScaleProblem => !('band' in problem)) ?? null;
}

/**
 * What « Ajouter une notation » opens on (§6, maquette `Main.dc.html`): two
 * empty bands, 0-50 and 50-100, plancher on the second.
 *
 * A FUNCTION and not a constant, for the reason `emptyProtocolDraft()` gives:
 * the object is handed straight to a draft that is then edited in place by the
 * reader's own copies, and a shared literal is one array every editor of one
 * browser session would be pushing bands into.
 *
 * Two bands because that is the minimum a scale can have, and the plancher on
 * the second because it is the ordinary case - the first would make a criterion
 * always filled, which §6 allows and H45 says the editor hints at rather than
 * forbids.
 */
export function emptyScoring(): CriterionScoringDraft {
  return {
    instruction: '',
    bands: [
      { upper: 50, text: '' },
      { upper: CRITERION_SCORING_TOP, text: '' },
    ],
    plancher: 1,
    passable: null,
  };
}
